import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { publicAuthApi } from '../lib/api';

const PublicAuthContext = createContext(null);

export const PublicAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsProfileCompletion, setNeedsProfileCompletion] = useState(false);
  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    
    // Don't check public auth if we're on admin routes
    if (window.location.pathname.startsWith('/admin')) {
      setLoading(false);
      return;
    }
    
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // First check if we have a token in localStorage (Safari/iOS fallback)
    const existingToken = localStorage.getItem('public_token');
    console.log('[Auth] checkAuth - has existing token:', !!existingToken);
    
    try {
      const response = await publicAuthApi.getMe();
      console.log('[Auth] checkAuth - /me success:', response.data?.email);
      setUser(response.data);
      // Save/refresh token to localStorage for Safari/iOS fallback
      if (response.data.access_token) {
        localStorage.setItem('public_token', response.data.access_token);
      }
      // Check if profile needs completion (Google users without DNI/birth_date)
      if (!response.data.dni || !response.data.birth_date) {
        setNeedsProfileCompletion(true);
      }
    } catch (error) {
      console.log('[Auth] checkAuth failed:', error?.response?.status, error?.response?.data?.detail);
      
      // If we have a token in localStorage but the request failed,
      // it might be because the cookie was blocked (Safari/iOS)
      // The axios interceptor should have already used the token from localStorage
      // If it still failed with 401, the token itself is invalid
      if (error?.response?.status === 401 && existingToken) {
        console.log('[Auth] Token was invalid, removing from localStorage');
        localStorage.removeItem('public_token');
      }
      
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await publicAuthApi.login(email, password);
    setUser(response.data.user);
    // Save token to localStorage as fallback for Safari/iOS
    if (response.data.access_token) {
      localStorage.setItem('public_token', response.data.access_token);
    }
    // Return full response including linked_accounts for welcome message
    return response.data;
  };

  const loginWithGoogleToken = async (credential) => {
    console.log('[Auth] Starting Google login...');
    const response = await publicAuthApi.googleCallback(credential);
    console.log('[Auth] Google callback response:', {
      hasUser: !!response.data.user,
      hasAccessToken: !!response.data.access_token,
      needsCompletion: response.data.needs_completion
    });
    
    setUser(response.data.user);
    setNeedsProfileCompletion(response.data.needs_completion);
    
    // Save token to localStorage as fallback for Safari/iOS
    if (response.data.access_token) {
      localStorage.setItem('public_token', response.data.access_token);
      console.log('[Auth] Saved access_token to localStorage');
    } else {
      console.warn('[Auth] No access_token in response!');
    }
    
    return response.data;
  };

  // Legacy method for backwards compatibility
  const loginWithGoogle = async (sessionId) => {
    const response = await publicAuthApi.googleSession(sessionId);
    setUser(response.data.user);
    setNeedsProfileCompletion(response.data.needs_completion);
    // Save token to localStorage as fallback for Safari/iOS
    if (response.data.access_token) {
      localStorage.setItem('public_token', response.data.access_token);
    }
    return response.data;
  };

  const completeProfile = async (dni, birthDate) => {
    const response = await publicAuthApi.completeGoogleProfile(dni, birthDate);
    setUser(response.data.user);
    setNeedsProfileCompletion(false);
    return response.data;
  };

  const updateProfile = async (data) => {
    const response = await publicAuthApi.updateProfile(data);
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await publicAuthApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setNeedsProfileCompletion(false);
      localStorage.removeItem('public_token');
    }
  };

  const refreshUser = async () => {
    try {
      const response = await publicAuthApi.getMe();
      setUser(response.data);
    } catch (error) {
      setUser(null);
    }
  };

  return (
    <PublicAuthContext.Provider value={{
      user,
      loading,
      isAuthenticated: !!user,
      needsProfileCompletion,
      login,
      loginWithEmail: login, // Alias for email login
      loginWithGoogle,
      loginWithGoogleToken,
      completeProfile,
      updateProfile,
      logout,
      refreshUser,
      checkAuth
    }}>
      {children}
    </PublicAuthContext.Provider>
  );
};

export const usePublicAuth = () => {
  const context = useContext(PublicAuthContext);
  if (!context) {
    throw new Error('usePublicAuth must be used within a PublicAuthProvider');
  }
  return context;
};
